import { useEffect, useState } from 'react';
import { Button, Card, Delta, TickerValue } from '../../components/UI';
import { AreaChart } from '../../components/Charts';
import {
  ActivityCard,
  AllocationCard,
  IndicesStrip,
  MergedMoversCard,
  MoversCard,
  NewsCard,
  WatchlistCard,
} from './DashboardCards';
import { PORTFOLIO, STOCKS_WITH_SERIES } from './data';
import type { TweakSettings } from '../../tweaks/types';
import { useBreakpoint } from '../../lib/useBreakpoint';
import {
  useMovers,
  useTrades,
  useWalletTxns,
  usePortfolioHistory,
  usePortfolioHoldings,
  usePortfolioSummary,
  type PortfolioRange,
  type PortfolioSummary,
} from '../../lib/dataHooks';
import type { MoverRow } from './DashboardCards';
import { useAuth } from '../../lib/auth';
import { getMarketStatus, sessionStartedToday } from '../../lib/marketHours';

interface DashboardProps {
  tweaks: Partial<TweakSettings>;
  onPickStock: (sym: string) => void;
}

export const Dashboard = ({ tweaks, onPickStock }: DashboardProps) => {
  // Default to 1M so the hero chart shows ~30 days of EOD points (the range
  // selector is currently hidden — wire it back when we re-enable). 1D would
  // collapse to a single EOD row pre-market and look broken.
  const [range, setRange] = useState('1M');
  const [livePortfolio, setLivePortfolio] = useState(PORTFOLIO.totalValue);
  const [liveStocks, setLiveStocks] = useState(STOCKS_WITH_SERIES);
  const liveOn = tweaks.liveTicks !== false;
  const heroH = tweaks.heroChartHeight ?? 210;
  const moverLayout = tweaks.moverLayout ?? 'split';
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isTablet = bp === 'tablet';
  const cols2 = isMobile || isTablet ? '1fr' : '1.6fr 1fr';
  const cols3 = isMobile ? '1fr' : isTablet ? '1fr 1fr' : '1.4fr 1fr 1fr';
  const pagePad = isMobile ? '20px 16px 60px' : '32px 32px 80px';

  const { user } = useAuth();
  const summaryQ = usePortfolioSummary(15_000);
  const holdingsQ = usePortfolioHoldings(15_000);
  const historyQ = usePortfolioHistory(range as PortfolioRange, 60_000);
  const realSummary = summaryQ.data;
  const liveSummary = !!realSummary;
  const greetingName = user?.name?.split(' ')[0] ?? 'Ali';
  // Stable color per symbol — same hash as DashboardCards.colorForSymbol.
  const colorForSymbol = (sym: string): string => {
    let h = 0;
    for (let i = 0; i < sym.length; i++) h = (h * 31 + sym.charCodeAt(i)) >>> 0;
    return `oklch(var(--tk-cat-l, 0.6) var(--tk-cat-c, 0.14) ${h % 360})`;
  };
  const allocationHoldings = holdingsQ.data
    ? holdingsQ.data.holdings.map((h) => ({
        sym: h.sym,
        qty: h.qty,
        avg: h.avg,
        color: h.color ?? colorForSymbol(h.sym),
      }))
    : undefined;

  useEffect(() => {
    if (!liveOn || liveSummary) return;
    const id = setInterval(() => {
      setLivePortfolio((p) => {
        const drift = (Math.random() - 0.45) * 600;
        return Math.max(0, p + drift);
      });
      setLiveStocks((prev) => {
        const next = prev.map((s) => ({ ...s }));
        const n = 1 + Math.floor(Math.random() * 2);
        for (let i = 0; i < n; i++) {
          const idx = Math.floor(Math.random() * next.length);
          const delta = (Math.random() - 0.48) * (next[idx].price * 0.0015);
          next[idx].price = Math.max(0.01, next[idx].price + delta);
        }
        return next;
      });
    }, 2200);
    return () => clearInterval(id);
  }, [liveOn, liveSummary]);

  const heroValue = realSummary?.totalValue ?? livePortfolio;
  const portfolioDayChange =
    realSummary?.dayChange ?? livePortfolio - (PORTFOLIO.totalValue - PORTFOLIO.dayChange);
  const portfolioDayPct =
    realSummary?.dayChangePct ??
    (portfolioDayChange / (PORTFOLIO.totalValue - PORTFOLIO.dayChange)) * 100;

  // Equity series for the chart. Built from EOD snapshots in the DB plus the
  // live "now" point from the summary. With only one EOD row we synthesize a
  // "yesterday close" point as `now - dayChange` so the chart has 2 points to
  // draw. Fixture series only used as the fall-back when there is no real
  // data at all (e.g. signed out / API offline).
  const { chartSeries, chartTimestamps } = (() => {
    if (!realSummary) return { chartSeries: undefined, chartTimestamps: undefined };
    const points = (historyQ.data?.points ?? []).map((p) => ({
      ts: new Date(p.ts).getTime(),
      value: p.totalValue,
    }));
    const now = realSummary.totalValue;
    const nowMs = Date.now();
    if (points.length === 0) {
      const yesterdayClose = now - realSummary.dayChange;
      return {
        chartSeries: [yesterdayClose, now],
        chartTimestamps: [nowMs - 86_400_000, nowMs],
      };
    }
    // Drop the last EOD if it's effectively the same as `now` (snapshot taken
    // moments before this render) to avoid a flat tail.
    const last = points[points.length - 1];
    const merged =
      Math.abs(last.value - now) < 1
        ? points
        : [...points, { ts: nowMs, value: now }];
    if (merged.length < 2) {
      const yesterdayClose = now - realSummary.dayChange;
      return {
        chartSeries: [yesterdayClose, now],
        chartTimestamps: [nowMs - 86_400_000, nowMs],
      };
    }
    return {
      chartSeries: merged.map((p) => p.value),
      chartTimestamps: merged.map((p) => p.ts),
    };
  })();

  // When PSX is closed, ranking by intraday Δ% gives a sea of zeros (last
  // == prev_close after our boot backfill). Switch to "last session" mode so
  // the cards show meaningful gainers/losers from the most recent trading day.
  // "Today" whenever a session has started today (including the Friday break
  // and after the close); the last completed session before the bell and at
  // weekends.
  const moversMode = sessionStartedToday() ? 'live' : 'lastSession';
  const moversQ = useMovers(10, 15_000, moversMode);
  // Live API rows from the worker's Redis snapshots. During market hours these
  // are intraday; outside market hours the worker keeps refreshing the same
  // close-of-session numbers so we still surface today's winners/losers.
  // Real data only — an empty list renders an empty state, never fixtures.
  const gainers: MoverRow[] = (moversQ.data?.gainers ?? []).map((m) => ({ sym: m.sym, price: m.last, change: m.changePct }));
  const losers: MoverRow[] = (moversQ.data?.losers ?? []).map((m) => ({ sym: m.sym, price: m.last, change: m.changePct }));
  const tradesQ = useTrades(20, 30_000);
  const txnsQ = useWalletTxns(30_000);

  // Fallback fixtures for the WatchlistCard when the API isn't available
  // (signed out / offline). The card fetches live data internally otherwise.
  const watchlistStocks = liveStocks.slice(0, 6);

  return (
    <div
      style={{ maxWidth: 1440, margin: '0 auto', padding: pagePad }}
      className="tk-fade-in"
    >
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 28,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--tk-ink-3)',
              letterSpacing: '0.02em',
              textTransform: 'uppercase',
              fontWeight: 600,
              marginBottom: 8,
            }}
          >
            {(() => {
              // PSX local date — the user is global, but the market is in PKT.
              const fmt = new Intl.DateTimeFormat('en-PK', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                timeZone: 'Asia/Karachi',
              });
              const open = getMarketStatus() === 'open';
              return `${fmt.format(new Date())} · Markets ${open ? 'open' : 'closed'}`;
            })()}
            <span
              style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: 99,
                background:
                  getMarketStatus() === 'open' ? 'var(--tk-up)' : 'var(--tk-ink-3)',
                marginLeft: 10,
                animation:
                  getMarketStatus() === 'open' ? 'tk-pulse 2s ease-in-out infinite' : undefined,
              }}
            />
          </div>
          <h1
            style={{
              fontSize: 30,
              fontWeight: 700,
              letterSpacing: '-0.025em',
              margin: 0,
              color: 'var(--tk-ink)',
            }}
          >
            {`${greetingByHour()}, ${greetingName}.`}
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" icon="deposit" size="md">
            Deposit
          </Button>
          <Button variant="primary" icon="plus" size="md">
            New trade
          </Button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: cols2, gap: 20, marginBottom: 20 }}>
        <PortfolioHero
          value={heroValue}
          dayChange={portfolioDayChange}
          dayPct={portfolioDayPct}
          range={range}
          setRange={setRange}
          chartHeight={heroH}
          summary={realSummary}
          chartSeries={chartSeries}
          chartTimestamps={chartTimestamps}
        />
        <AllocationCard holdings={allocationHoldings} />
      </div>

      <IndicesStrip showSparkline={tweaks.showSparklines !== false} />

      {moverLayout === 'split' ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: cols3,
            gap: 20,
            marginTop: 20,
          }}
        >
          <WatchlistCard stocks={watchlistStocks} onPick={onPickStock} />
          <MoversCard
            title={moversMode === 'lastSession' ? 'Top gainers · last session' : 'Top gainers · today'}
            stocks={gainers}
            onPick={onPickStock}
          />
          <MoversCard
            title={moversMode === 'lastSession' ? 'Top losers · last session' : 'Top losers · today'}
            stocks={losers}
            onPick={onPickStock}
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: cols2, gap: 20, marginTop: 20 }}>
          <WatchlistCard stocks={watchlistStocks} onPick={onPickStock} />
          <MergedMoversCard
            gainers={gainers}
            losers={losers}
            onPick={onPickStock}
            titleSuffix={moversMode === 'lastSession' ? 'last session' : 'today'}
          />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: cols2, gap: 20, marginTop: 20 }}>
        <NewsCard />
        <ActivityCard trades={tradesQ.data?.trades ?? []} txns={txnsQ.data?.transactions ?? []} />
      </div>
    </div>
  );
};

interface HeroProps {
  value: number;
  dayChange: number;
  dayPct: number;
  range: string;
  setRange: (r: string) => void;
  chartHeight?: number;
  summary?: PortfolioSummary | null;
  /** Real equity series from `/portfolio/history`. Falls back to the fixture
   *  series when undefined or too short to render meaningfully. */
  chartSeries?: number[];
  /** Parallel timestamps array for the AreaChart hover tooltip. */
  chartTimestamps?: number[];
}

const greetingByHour = () => {
  const h = new Date().getHours();
  if (h < 5) return 'Good night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};

const PortfolioHero = ({
  value,
  dayChange,
  dayPct,
  range,
  setRange,
  chartHeight = 210,
  summary,
  chartSeries,
  chartTimestamps,
}: HeroProps) => {
  const isLive = !!summary;
  // Before the bell and at weekends the API reports the last completed
  // session's move rather than a meaningless zero — label it honestly.
  const dayOf = summary?.dayOf ?? 'today';
  const dayLabel = dayOf === 'lastSession' ? 'last session' : 'today';
  const marketOpen = getMarketStatus() === 'open';
  const showLive = isLive && marketOpen;
  const badgeLabel = !isLive ? 'DEMO' : marketOpen ? 'LIVE' : 'MARKET CLOSED';
  const badgeTitle = !isLive
    ? 'Demo values (sign in for live)'
    : marketOpen
      ? 'Live values from your account'
      : 'PSX is closed — values reflect last close';
  const invested = summary?.invested ?? PORTFOLIO.invested;
  const totalGain = summary?.totalGain ?? PORTFOLIO.totalGain;
  const totalGainPct = summary?.totalGainPct ?? PORTFOLIO.totalGainPct;
  const cash = summary?.cash ?? PORTFOLIO.cash;
  return (
    <Card padding={28} style={{ position: 'relative', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--tk-ink-3)',
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            Total portfolio value
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                padding: '2px 7px',
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: '0.06em',
                fontFamily: 'var(--tk-mono)',
                background: showLive ? 'var(--tk-up-soft)' : 'var(--tk-surface-2)',
                color: showLive ? 'var(--tk-up)' : 'var(--tk-ink-3)',
                borderRadius: 4,
              }}
              title={badgeTitle}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 3,
                  background: showLive ? 'var(--tk-up)' : 'var(--tk-ink-3)',
                  animation: showLive ? 'tk-pulse 1.6s var(--tk-ease) infinite' : undefined,
                }}
              />
              {badgeLabel}
            </span>
          </div>
          <div
            style={{
              fontSize: 44,
              lineHeight: 1,
              fontWeight: 600,
              letterSpacing: '-0.03em',
              color: 'var(--tk-ink)',
            }}
          >
            <span
              style={{ fontSize: 22, color: 'var(--tk-ink-3)', marginRight: 6, fontWeight: 500 }}
            >
              PKR
            </span>
            <TickerValue value={value} decimals={2} big />
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginTop: 14 }}>
            <Delta value={dayPct} size="lg" />
            <span
              className="mono"
              style={{
                color: dayChange >= 0 ? 'var(--tk-up)' : 'var(--tk-down)',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {dayChange >= 0 ? '+' : ''}
              {dayChange.toLocaleString('en-PK', { maximumFractionDigits: 2 })} {dayLabel}
            </span>
            <span style={{ color: 'var(--tk-ink-3)', fontSize: 13 }}>
              · All-time {totalGainPct >= 0 ? '+' : ''}
              {totalGainPct.toFixed(2)}%
            </span>
          </div>
        </div>

        {/* Range selector hidden until we accumulate enough EOD snapshots
            for multi-day timeframes to be meaningful. We snapshot once per
            trading day at 15:35 PKT (plus on each trade), so 1W/1M/etc.
            need ≥1 week of snapshots before we re-enable this. */}
      </div>

      <div style={{ marginTop: 24, marginLeft: -8, marginRight: -8 }}>
        <AreaChart
          data={chartSeries && chartSeries.length >= 2 ? chartSeries : PORTFOLIO.series}
          timestamps={chartSeries && chartSeries.length >= 2 ? chartTimestamps : undefined}
          height={chartHeight}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 0,
          marginTop: 8,
          paddingTop: 20,
          borderTop: '1px solid var(--tk-line-2)',
        }}
      >
        {[
          { label: 'Invested', value: invested, prefix: 'PKR ' },
          { label: 'Total gain', value: totalGain, prefix: 'PKR ', up: totalGain >= 0 },
          { label: 'Available cash', value: cash, prefix: 'PKR ' },
          { label: dayOf === 'lastSession' ? 'Last session P&L' : 'Day P&L', value: dayChange, prefix: 'PKR ', up: dayChange >= 0, signed: true },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              paddingLeft: i === 0 ? 0 : 20,
              borderLeft: i === 0 ? 'none' : '1px solid var(--tk-line-2)',
            }}
          >
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--tk-ink-3)',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                marginBottom: 6,
              }}
            >
              {s.label}
            </div>
            <div
              className="mono"
              style={{
                fontSize: 17,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                color:
                  s.up !== undefined
                    ? s.up
                      ? 'var(--tk-up)'
                      : 'var(--tk-down)'
                    : 'var(--tk-ink)',
              }}
            >
              {s.value < 0 ? '−' : s.signed ? '+' : ''}
              {s.prefix}
              {Math.abs(s.value).toLocaleString('en-PK', { maximumFractionDigits: 0 })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
